import { useEffect, useState, type CSSProperties } from "react";
import { belegAkzent } from "@talentkontor/domain";
import { useLogoSrc } from "../../components/shell/branding";
import {
  anteil, bioFor, fokusPunkt, genderName, handleUrl, kitAbsender, kitDate, kitMoney, kitSpalten, kitStamp, kitSummary, kitZielgruppe, kitZusammengestellt, kurz, logoMass, percentOfBp, platformName, rateLabel, rolesFor, tk, topicsFor,
  type KitLang, type KitSpalte, type KitTalent, type KitView,
} from "./kit-model";
import "./kit.css";

export type KitBildLader = (talentId: string, assetId?: string) => Promise<Blob>;

/**
 * Media-Kit bzw. Pitch-Liste, wie die Marke sie sieht (0074, neu gestaltet 0214): je Creator ein helles Blatt nach dem Vorbild
 * eines gedruckten Media-Kits. Logo der Agentur, großer Name mit Rollenzeile, Foto-Collage, Themen, „Trusted by“ mit Marken-Logos,
 * „Über mich“, Zielgruppe und Kennzahlen nebeneinander (jeweils mit Stand), Preise und Kontakt. Deutsch oder Englisch umschaltbar,
 * als PDF herunterladbar (mit Erstelldatum) und druckbar. Das Blatt ist immer hell, damit Logos und Fotos gleich aussehen.
 */
export function KitAnsicht({ view, loadImage, embedded = false, onLang }: { view: KitView; loadImage?: KitBildLader; embedded?: boolean; onLang?: (lang: KitLang) => void }) {
  const [lang, setLang] = useState<KitLang>(view.language);
  const [pdf, setPdf] = useState<"idle" | "busy" | "error">("idle");
  // Rahmen der Seite (Umschalter Hell/Dunkel) in derselben Sprache
  useEffect(() => { onLang?.(lang); }, [lang, onLang]);
  const t = tk(lang);
  const logo = useLogoSrc(view.agency.logoPath);
  const akzent = belegAkzent(view.agency.brandColor) ?? "#1d1d1b";
  const sum = kitSummary(view);
  const title = view.kind === "pitch"
    ? view.title ?? (view.brand ? t(`Creator-Auswahl für ${view.brand}`, `Creator selection for ${view.brand}`) : t("Creator-Auswahl", "Creator selection"))
    : view.talents[0]?.name ?? view.title ?? "Media-Kit";
  const stand = kitStamp(view.createdAt, lang);
  // Inhaber 24.10.2026: gegenüber Marken der Firmenname (sonst Anzeigename); „Zusammengestellt von“ nur unter einem Logo-Kopf
  const absender = kitAbsender(view);
  const zusammengestellt = kitZusammengestellt(view, lang, !!logo);

  async function herunterladen() {
    if (pdf === "busy") return;
    setPdf("busy");
    try {
      const { kitPdfHerunterladen } = await import("./kit-pdf");
      await kitPdfHerunterladen(view, lang, { loadImage, logoUrl: logo ?? null });
      setPdf("idle");
    } catch {
      setPdf("error");
    }
  }

  return (
    <div className="stack tk-kit" lang={lang} style={{ "--kit-akzent": akzent } as CSSProperties}>
      {!embedded && <title>{`${title} · ${absender || "Media-Kit"}`}</title>}
      <div className="tk-kit-leiste tk-kit-noprint">
        <div className="row" role="group" aria-label={t("Sprache", "Language")}>
          <button type="button" aria-pressed={lang === "de"} onClick={() => setLang("de")}>Deutsch</button>
          <button type="button" aria-pressed={lang === "en"} onClick={() => setLang("en")}>English</button>
        </div>
        <div className="row">
          <button type="button" className="primary" disabled={pdf === "busy"} aria-busy={pdf === "busy"} onClick={() => void herunterladen()}>
            {pdf === "busy" ? t("PDF wird erstellt …", "Creating PDF …") : t("Als PDF herunterladen", "Download as PDF")}
          </button>
          <button type="button" onClick={() => window.print()}>{t("Drucken", "Print")}</button>
        </div>
      </div>
      {pdf === "error" && <p className="tk-error tk-kit-noprint" role="alert">{t("Das PDF ließ sich nicht erstellen. Bitte noch einmal versuchen oder „Drucken“ wählen.", "The PDF could not be created. Please try again or choose “Print”.")}</p>}

      {(view.kind === "pitch" || view.message) && (
        <header className="tk-kit-vorspann">
          {view.kind === "pitch" && (
            <>
              <div className="eyebrow">{t("Pitch-Liste", "Pitch list")}{view.brand ? ` · ${t("für", "for")} ${view.brand}` : ""}</div>
              {embedded ? <h3>{title}</h3> : <h1>{title}</h1>}
              <p className="source-note">{`${sum.creators} ${lang === "en" ? (sum.creators === 1 ? "creator" : "creators") : "Creator"}${sum.followers ? ` · ${kurz(sum.followers, lang)} ${t("Follower zusammen", "followers combined")}` : ""}`}</p>
            </>
          )}
          {view.message && <blockquote className="tk-kit-nachricht">{view.message}</blockquote>}
        </header>
      )}

      {view.talents.map((c, i) => (
        <Blatt key={c.talent_id} c={c} view={view} lang={lang} logo={logo ?? null} loadImage={loadImage} stand={stand} ueberschrift={view.kind === "pitch" || embedded || i > 0 ? "h2" : "h1"} />
      ))}

      <footer className="source-note tk-kit-hinweis">
        {zusammengestellt ? `${zusammengestellt}. ` : ""}{t(`Erstellt am ${stand}. Vertraulich, gültig bis ${kitDate(view.expiresAt, lang)}.`,
          `Created on ${stand}. Confidential, valid until ${kitDate(view.expiresAt, lang)}.`)}
        {" "}{t("Kennzahlen von Hand sind Angaben der Agentur zum genannten Stand; Instagram-Werte stammen aus dem verbundenen Konto und werden nur mit Einwilligung des Creators gezeigt.",
          "Manually entered figures are provided by the agency as of the stated date; Instagram figures come from the connected account and are shown only with the creator's consent.")}
      </footer>
    </div>
  );
}

/** Bild aus dem privaten Speicher: lädt einmal, gibt die Adresse beim Verlassen frei. */
function useBild(load: (() => Promise<Blob>) | null, key: string): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!load) return;
    let alive = true;
    let url: string | null = null;
    load().then((b) => {
      if (!alive || !b.type.startsWith("image/")) return;
      url = URL.createObjectURL(b);
      setSrc(url);
    }).catch(() => undefined);
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
      setSrc(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return src;
}

function Foto({ c, id, loadImage, className }: { c: KitTalent; id?: string; loadImage?: KitBildLader; className: string }) {
  const src = useBild(loadImage ? () => loadImage(c.talent_id, id) : null, `${c.talent_id}:${id ?? "profil"}`);
  const f = fokusPunkt(id ? c.photoFocus[id] : null);
  return <div className={className}>{src ? <img src={src} alt="" style={{ objectPosition: `${f.x * 100}% ${f.y * 100}%` }} /> : null}</div>;
}

/** Logo in fester Zelle; die Maße kommen aus dem Seitenverhältnis (gleiche Fläche), damit kein Logo die Zelle sprengt. */
function LogoBild({ c, id, label, loadImage, klein }: { c: KitTalent; id: string; label: string | null; loadImage?: KitBildLader; klein: boolean }) {
  const src = useBild(loadImage ? () => loadImage(c.talent_id, id) : null, `${c.talent_id}:${id}`);
  const [mass, setMass] = useState<{ w: number; h: number } | null>(null);
  return (
    <li className="tk-kit-logozelle">
      {src
        ? <img src={src} alt={label ?? ""} style={mass ? { width: mass.w, height: mass.h } : { visibility: "hidden", width: 1, height: 1 }}
            onLoad={(e) => setMass(klein ? logoMass(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight, 50, 150, 34) : logoMass(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight))} />
        : label ? <span>{label}</span> : null}
    </li>
  );
}

function Blatt({ c, view, lang, logo, loadImage, stand, ueberschrift: H }: { c: KitTalent; view: KitView; lang: KitLang; logo: string | null; loadImage?: KitBildLader; stand: string; ueberschrift: "h1" | "h2" }) {
  const t = tk(lang);
  const bio = bioFor(c, lang);
  const rollen = rolesFor(c, lang);
  const themen = topicsFor(c, lang);
  const spalten = kitSpalten(c);
  const z = kitZielgruppe(c);
  const fotos = c.photos.slice(0, 7);
  const marken = [...new Set((c.references ?? []).map((r) => r.brand).filter((b): b is string => !!b))];
  const zeilen = ([
    { label: t("Follower", "Followers"), wert: (s) => kurz(s.followers, lang), da: spalten.some((s) => s.followers != null) },
    { label: t("Aufrufe 30 Tage", "Views last 30 days"), wert: (s) => kurz(s.views30, lang), da: spalten.some((s) => s.views30 != null) },
    { label: t("Reichweite 30 Tage", "Reach last 30 days"), wert: (s) => kurz(s.reach30, lang), da: spalten.some((s) => s.reach30 != null) },
    { label: t("Interaktionsrate", "Engagement rate"), wert: (s) => percentOfBp(s.engagementBp, lang), da: spalten.some((s) => s.engagementBp != null) },
    { label: t("Ø Aufrufe je Beitrag", "Avg. views per post"), wert: (s) => kurz(s.avgViews, lang), da: spalten.some((s) => s.avgViews != null) },
    { label: t("Story-Aufrufe", "Story views"), wert: (s) => kurz(s.storyViews, lang), da: spalten.some((s) => s.storyViews != null) },
  ] as { label: string; wert: (s: KitSpalte) => string; da: boolean }[]).filter((r) => r.da);
  const kontakte = spalten.filter((s) => s.handle).map((s) => ({ platform: s.platform, handle: s.handle!.startsWith("@") ? s.handle! : `@${s.handle}` }));
  for (const [p, h] of Object.entries(c.handles)) if (!kontakte.some((k) => k.platform === p)) kontakte.push({ platform: p, handle: h.startsWith("@") ? h : `@${h}` });

  return (
    <article className="tk-kit-blatt" aria-labelledby={`kit-${c.talent_id}`}>
      <div className="tk-kit-blattkopf">
        <span className="tk-kit-marke">{view.kind === "pitch" ? "" : `Media-Kit${view.brand ? ` · ${t("für", "for")} ${view.brand}` : ""}`}</span>
        {logo ? <img className="tk-kit-logo" src={logo} alt={kitAbsender(view)} /> : <strong className="tk-kit-agentur">{kitAbsender(view)}</strong>}
      </div>

      <div className="tk-kit-titel">
        {fotos.length === 0 && c.has_image && <Foto c={c} loadImage={loadImage} className="tk-kit-portrait" />}
        <H id={`kit-${c.talent_id}`} className="tk-kit-name">{c.name}</H>
        {rollen && <p className="tk-kit-rollen">{rollen}</p>}
      </div>

      {fotos.length > 0 && (
        <div className={`tk-kit-collage tk-kit-collage-${fotos.length}`}>
          {fotos.map((id, i) => <Foto key={id} c={c} id={id} loadImage={loadImage} className={`tk-kit-foto tk-kit-foto-${i + 1}`} />)}
        </div>
      )}

      {themen.length > 0 && <p className="tk-kit-themen"><strong>{t("Themenschwerpunkte", "Key topics")}:</strong> {themen.join(", ")}</p>}

      {(c.logos.length > 0 || marken.length > 0) && (
        <section className="tk-kit-trusted" aria-label="Trusted by">
          <div className="tk-kit-trusted-kopf"><strong>TRUSTED BY</strong>{c.logos.length > 0 && <small>{t("und viele mehr", "and many more")}</small>}</div>
          {c.logos.length > 0 && (
            <ul className={`tk-kit-logos${c.logos.length > 12 ? " viele" : ""}`}>
              {c.logos.map((l) => <LogoBild key={l.id} c={c} id={l.id} label={l.label} loadImage={loadImage} klein={c.logos.length > 12} />)}
            </ul>
          )}
          {c.logos.length === 0 && <p className="tk-kit-markennamen">{marken.join("  ·  ")}</p>}
        </section>
      )}

      {bio.text && (
        <section className="tk-kit-abschnitt">
          <h3>{t("Über mich", "About me")}</h3>
          <p className="tk-kit-bio">{bio.text}{bio.fallback ? <small> ({lang === "en" ? "in German" : "auf Englisch"})</small> : null}</p>
        </section>
      )}

      {(z || zeilen.length > 0) && (
        <div className={`tk-kit-zwei${z && zeilen.length > 0 && spalten.length <= 2 ? "" : " allein"}${spalten.length >= 3 ? " viele" : ""}`}>
          {z && (
            <section className="tk-kit-abschnitt">
              <h3>{t("Zielgruppe", "Audience")}</h3>
              <div className="tk-kit-zielgruppe">
                {z.gender.length > 0 && (
                  <div>
                    <h4>{t("Geschlecht", "Gender")}</h4>
                    {z.gender.map((g) => <p key={g.key} className="tk-kit-zeile"><span>{genderName(g.key, lang)}</span><strong>{anteil(g.share, lang)}</strong></p>)}
                  </div>
                )}
                {z.age.length > 0 && (
                  <div>
                    <h4>{t("Alter", "Age")}</h4>
                    {z.age.map((a) => (
                      <p key={a.key} className="tk-kit-balkenzeile"><span>{a.key}</span><span className="tk-kit-balken" aria-hidden="true"><i style={{ width: `${Math.max(4, Math.min(100, a.share * 100))}%` }} /></span><strong>{anteil(a.share, lang)}</strong></p>
                    ))}
                  </div>
                )}
                {z.country.length > 0 && (
                  <div className="breit">
                    <h4>{t("Länder", "Countries")}</h4>
                    <div className="tk-kit-laender">{z.country.map((l) => <p key={l.key} className="tk-kit-zeile"><span>{l.key}</span><strong>{anteil(l.share, lang)}</strong></p>)}</div>
                  </div>
                )}
              </div>
              <small className="tk-kit-stand">{z.connected ? t("Aus dem verbundenen Instagram-Konto", "From the connected Instagram account") : t("Angabe der Agentur", "Provided by the agency")} · {t("Stand", "as of")} {kitDate(z.asOf, lang)}</small>
            </section>
          )}
          {zeilen.length > 0 && (
            <section className="tk-kit-abschnitt">
              <h3>{t("Reichweite und Leistung", "Reach and performance")}</h3>
              <div className="tk-kit-tabelle">
                <table>
                  <caption className="sr-only">{t(`Kennzahlen von ${c.name}`, `Metrics of ${c.name}`)}</caption>
                  <thead><tr><td />{spalten.map((s, i) => <th key={i} scope="col">{platformName(s.platform, lang)}</th>)}</tr></thead>
                  <tbody>
                    {zeilen.map((r) => <tr key={r.label}><th scope="row">{r.label}</th>{spalten.map((s, i) => <td key={i}>{r.wert(s)}</td>)}</tr>)}
                    <tr className="tk-kit-standzeile"><th scope="row">{t("Stand", "As of")}</th>{spalten.map((s, i) => <td key={i}>{s.asOf ? kitDate(s.asOf, lang) : "–"}</td>)}</tr>
                  </tbody>
                </table>
              </div>
              {spalten.some((s) => s.connected) && <small className="tk-kit-stand">{t("Instagram aus dem verbundenen Konto, mit Einwilligung des Creators.", "Instagram from the connected account, with the creator's consent.")}</small>}
            </section>
          )}
        </div>
      )}

      {c.references && c.references.length > 0 && c.logos.length > 0 && (
        <section className="tk-kit-abschnitt">
          <h3>{t("Ausgewählte Kooperationen", "Selected collaborations")}</h3>
          <ul className="tk-kit-referenzen">
            {c.references.map((r, i) => (
              <li key={i}><strong>{r.brand ?? t("Marke", "Brand")}</strong>{r.year ? ` · ${r.year}` : ""}{r.platforms?.length ? ` · ${r.platforms.map((p) => platformName(p, lang)).join(", ")}` : ""}{r.note ? <> · {r.note}</> : null}</li>
            ))}
          </ul>
        </section>
      )}

      {c.rates && c.rates.length > 0 && (
        <section className="tk-kit-abschnitt">
          <h3>{t("Preise", "Rates")}</h3>
          <ul className="tk-kit-preise">
            {c.rates.map((r, i) => <li key={i}><span>{rateLabel(r, lang)}</span><strong>{kitMoney(r.amount_cents, r.currency, lang)}</strong></li>)}
          </ul>
          <small className="tk-kit-stand">{t("Netto, zuzüglich gesetzlicher Umsatzsteuer, sofern anfallend. Freibleibend.", "Net, plus statutory VAT where applicable. Subject to change.")}</small>
        </section>
      )}

      <footer className="tk-kit-fuss">
        {view.agency.email ? <a href={`mailto:${view.agency.email}`}>{view.agency.email}</a> : <span>{kitAbsender(view)}</span>}
        <span className="tk-kit-konten">
          {kontakte.map((k) => {
            const url = handleUrl(k.platform, k.handle);
            const text = `${platformName(k.platform, lang)} ${k.handle}`;
            return url ? <a key={`${k.platform}${k.handle}`} href={url} target="_blank" rel="noopener noreferrer">{text}</a> : <span key={`${k.platform}${k.handle}`}>{text}</span>;
          })}
        </span>
        <small>{t(`Erstellt am ${stand}`, `Created on ${stand}`)}</small>
      </footer>
    </article>
  );
}
